import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { OpsService } from '../../Modules/Ops/Services/ops.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import {
  RETRY_ALL_APPLIED_MESSAGE,
  RETRY_APPLIED_MESSAGE,
  ROLE_OPS_RETRY,
} from '../../Consts/ops.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  FailedJobListResponse,
  FailedJobResponse,
  OpsHealthEnvelope,
  OpsHealthResponse,
  OpsSchedulerCommandResponse,
} from '../../Interfaces/api/backoffice/ops.interface';

@Injectable()
export class OpsFacade {
  private readonly _opsService = inject(OpsService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _isMutating = signal<boolean>(false);

  private readonly _health = httpResource<LaravelDefaultResponse<OpsHealthEnvelope>>(() =>
    this._opsService.healthRequest(),
  );

  private readonly _failedJobs = httpResource<LaravelDefaultResponse<FailedJobListResponse>>(() =>
    this._opsService.failedJobsRequest(),
  );

  readonly health = computed<OpsHealthResponse | null>(() =>
    this._opsService.toHealth(this._resourceState.valueOf(this._health)),
  );

  readonly healthState = computed<MetricState>(() =>
    this._resourceState.metricState(this._health.status()),
  );

  readonly overall = computed<string>(() => this.health()?.overall ?? 'unknown');

  readonly schedulerCommands = computed<OpsSchedulerCommandResponse[]>(
    () => this.health()?.scheduler.commands ?? [],
  );

  readonly failedJobs = computed<FailedJobResponse[]>(() =>
    this._opsService.toFailedJobs(this._resourceState.valueOf(this._failedJobs)),
  );

  readonly queueName = computed<string>(
    () => this._opsService.toQueueName(this._resourceState.valueOf(this._failedJobs)) || (this.health()?.queue_name ?? ''),
  );

  readonly failedJobsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._failedJobs.status()),
  );

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly canRetry = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_OPS_RETRY) && !this._isMutating(),
  );

  reload(): void {
    this._health.reload();
    this._failedJobs.reload();
  }

  retry(uuid: string): void {
    if (!this.canRetry()) {
      return;
    }

    this._isMutating.set(true);

    this._opsService
      .retry(uuid)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(RETRY_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  retryAll(): void {
    if (!this.canRetry()) {
      return;
    }

    this._isMutating.set(true);

    this._opsService
      .retryAll()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(RETRY_ALL_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
